import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MapPin } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { images } from "../config/images";
import { siteConfig, fullAddress } from "../config/site";
import { AvailabilityButton } from "./BookingButton";

const ease = [0.22, 1, 0.36, 1] as const;

export const Hero = () => {
  const { t } = useLanguage();
  const reduce = useReducedMotion();
  const ref = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  // Nur Deckkraft der Abdunklung ändert sich beim Scrollen – das Foto selbst bleibt gestochen scharf.
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.45]);

  return (
    <section ref={ref} className="relative h-[100svh] min-h-[640px] w-full overflow-hidden bg-charcoal" data-testid="hero-section">
      {/* Foto in voller Qualität – kein Skalieren, kein Filter */}
      <img src={images.hero} alt={t.hero.imageAlt} className="absolute inset-0 h-full w-full object-cover" loading="eager" fetchPriority="high" />

      {/* Farbverlauf für Lesbarkeit */}
      <motion.div aria-hidden style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/20 to-black/75" />
      {/* Feine Körnung (Grain) */}
      <div aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.12] mix-blend-overlay [background-image:url('data:image/svg+xml,%3Csvg%20xmlns=%22http://www.w3.org/2000/svg%22%20width=%22140%22%20height=%22140%22%3E%3Cfilter%20id=%22n%22%3E%3CfeTurbulence%20type=%22fractalNoise%22%20baseFrequency=%220.85%22%20numOctaves=%222%22/%3E%3C/filter%3E%3Crect%20width=%22100%25%22%20height=%22100%25%22%20filter=%22url(%23n)%22/%3E%3C/svg%3E')]" />

      {!reduce && (
        <>
          {/* „Türen öffnen sich" – Reveal beim Laden (liegt über dem Foto, verändert es nicht) */}
          <motion.div aria-hidden className="absolute inset-y-0 left-0 z-20 w-1/2 origin-left bg-charcoal" initial={{ scaleX: 1 }} animate={{ scaleX: 0 }} transition={{ duration: 1.1, ease, delay: 0.15 }} />
          <motion.div aria-hidden className="absolute inset-y-0 right-0 z-20 w-1/2 origin-right bg-charcoal" initial={{ scaleX: 1 }} animate={{ scaleX: 0 }} transition={{ duration: 1.1, ease, delay: 0.15 }} />
          {/* dünne Mittellinie, die mit aufgeht */}
          <motion.div aria-hidden className="absolute inset-y-0 left-1/2 z-30 w-px -translate-x-1/2 bg-cream/60" initial={{ scaleY: 1, opacity: 1 }} animate={{ scaleY: 0, opacity: 0 }} transition={{ duration: 0.9, ease, delay: 0.15 }} />
          {/* einmaliger Licht-Sweep */}
          <motion.div
            aria-hidden
            className="pointer-events-none absolute inset-0 z-[15]"
            initial={{ x: "-130%" }} animate={{ x: "130%" }} transition={{ duration: 1.5, ease, delay: 1.15 }}
            style={{ background: "linear-gradient(105deg, transparent 42%, rgba(255,255,255,0.22) 50%, transparent 58%)" }}
          />
        </>
      )}

      {/* Content unten links */}
      <div className="container-x absolute inset-x-0 bottom-0 z-10 pb-14 md:pb-20">
        <motion.p
          className="font-serif text-lg italic text-cream/85 md:text-xl"
          initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease, delay: reduce ? 0.1 : 1.05 }}
          data-testid="hero-eyebrow"
        >
          {t.hero.eyebrow}
        </motion.p>

        <h1 className="mt-3 max-w-4xl font-display text-[2.9rem] font-medium leading-[0.98] tracking-[-0.03em] text-cream sm:text-6xl lg:text-7xl xl:text-8xl" data-testid="hero-headline">
          {t.hero.headline.map((line, idx) => (
            <span key={idx} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: reduce ? 0 : "110%" }} animate={{ y: 0 }}
                transition={{ duration: 1, ease, delay: (reduce ? 0.15 : 1.2) + idx * 0.12 }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          className="mt-8 flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between"
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease, delay: reduce ? 0.3 : 1.55 }}
        >
          <div className="max-w-md">
            <p className="flex items-start gap-2 text-sm text-cream/80" data-testid="hero-address">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-cream/60" aria-hidden />
              <span>{fullAddress()}, {siteConfig.address.country}</span>
            </p>
            <p className="mt-4 text-sm leading-relaxed text-cream/70" data-testid="hero-info-line">{t.hero.infoLine}</p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <AvailabilityButton variant="primary" testId="hero-availability-button" className="!bg-cream !text-charcoal hover:!bg-white !px-7 !py-3.5" />
            <Link to="/zimmer" className="btn-outline !border-cream/40 !text-cream hover:!bg-cream hover:!text-charcoal !px-7 !py-3.5" data-testid="hero-rooms-button">
              {t.cta.viewRooms}
            </Link>
          </div>
        </motion.div>
      </div>

      <motion.div
        className="absolute bottom-6 left-1/2 z-10 hidden -translate-x-1/2 lg:block"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: reduce ? 0.5 : 2 }}
      >
        <span className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-cream/60">
          <ArrowDown className="h-3.5 w-3.5 animate-bounce" aria-hidden /> {t.hero.scroll}
        </span>
      </motion.div>
    </section>
  );
};
